import React, { useCallback, useMemo } from 'react';
import classNames from 'classnames';
import { FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { Typography, DineInIcon, TakeAwayIcon, DriveThroughIcon } from '@qlub-dev/qlub-kit';
import { useTranslation } from '@clubpay/customer-common-module/src/hook/translations';
import { useQlubRouter } from '@clubpay/customer-common-module/src/hook/router';
import { useVendor } from '@clubpay/customer-common-module/src/context/vendor';
import { convertMultiLocaleStringToObject } from '@clubpay/customer-common-module/src/component/MultiLocale';
import { DiningOptionEnum } from '@clubpay/customer-common-module/src/repository/vendor/type';
import { checkEmpty } from '@/services/utils/k_qsr';

import styles from './index.module.scss';

interface IProps {
    inline?: boolean;
    value: DiningOptionEnum | undefined;
    onChange: (val: DiningOptionEnum | undefined) => void;
}
const diningOptionDefault: { value?: DiningOptionEnum } = {
    value: undefined,
};

export const resetDiningOption = () => {
    diningOptionDefault.value = undefined;
};

const QSRDiningOptions = ({ inline, value, onChange }: IProps) => {
    const { t } = useTranslation('common');
    const { lang } = useQlubRouter();
    const { vendor } = useVendor();

    const withIcon = vendor?.orderConfig?.diningOptionEnableIcons;

    const changeHandler = useCallback((selectedOption: DiningOptionEnum) => {
        onChange(selectedOption);
    }, []);

    const defaultDineOptionTranslation = useCallback(
        (val: DiningOptionEnum) => {
            switch (val) {
                case DiningOptionEnum.DineIn:
                    return t('Dine-in');
                case DiningOptionEnum.TakeAway:
                    return t('Takeaway');
                case DiningOptionEnum.DriveThrough:
                    return t('Drive-through');
                default:
                    return t('Unknown');
            }
        },
        [lang, t],
    );

    const getItemTranslation = useCallback(
        (val: DiningOptionEnum) => {
            const defaultText = defaultDineOptionTranslation(val);
            return (
                convertMultiLocaleStringToObject(vendor?.orderConfig?.diningOptionCustomText?.[val] || '', defaultText)[
                    lang
                ] || defaultText
            );
        },
        [vendor, defaultDineOptionTranslation],
    );

    const handleSelect = useCallback((event: SelectChangeEvent) => {
        onChange(event.target.value as DiningOptionEnum);
    }, []);

    const options: DiningOptionEnum[] = checkEmpty(vendor?.orderConfig?.diningOptionList) || [
        DiningOptionEnum.DineIn,
        DiningOptionEnum.TakeAway,
    ];

    const dineOptionIconMap = useMemo<Record<string, any>>(() => {
        return {
            [DiningOptionEnum.DineIn]: <DineInIcon />,
            [DiningOptionEnum.TakeAway]: <TakeAwayIcon />,
            [DiningOptionEnum.DriveThrough]: <DriveThroughIcon />,
        };
    }, []);

    const inlineContent = useMemo(() => {
        return (
            <div className={styles.inline}>
                <Typography typo="title_md" className={styles.title}>
                    {t('Order type')}
                </Typography>
                <FormControl sx={{ width: '100%' }}>
                    <Select
                        value={value}
                        onChange={handleSelect}
                        displayEmpty
                        sx={{
                            boxShadow: 'none',
                            background: 'var(--dim)',
                            borderRadius: '8px',
                            '.MuiOutlinedInput-notchedOutline': { border: 0 },
                            '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                                border: 0,
                            },
                            '&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                border: 0,
                            },
                            '.MuiOutlinedInput-input': {
                                padding: '10px 12px',
                                borderRadius: '8px',
                            },
                        }}
                    >
                        {options.map((item) => (
                            <MenuItem key={item} value={item}>
                                {getItemTranslation(item)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
        );
    }, [value]);

    const content = useMemo(() => {
        return (
            <div className={styles.body}>
                <div className={styles.header}>{t('Please select your preferred option')}</div>

                {options.map((option) => (
                    <div
                        key={option}
                        className={classNames(styles.btn, {
                            [styles.noIcon]: !withIcon,
                        })}
                        onClick={() => changeHandler(option)}
                    >
                        {withIcon ? dineOptionIconMap[option] || null : null}
                        <div className={styles.text}>
                            <Typography className={styles.title}>{getItemTranslation(option)}</Typography>
                        </div>
                    </div>
                ))}
            </div>
        );
    }, [inline, options, withIcon, dineOptionIconMap, getItemTranslation, changeHandler, t]);

    if (inline) {
        return inlineContent;
    }

    return <div className={styles.container}>{content}</div>;
};

export default QSRDiningOptions;
